import { Edit05 } from '#app/components/icons/duotone'
import { ChevronRight, Folder } from '#app/components/icons/solid'
import { useState } from 'react'
import {
	Button,
	Collection,
	Link,
	Tree,
	TreeItem,
	TreeItemContent,
	type TreeProps,
} from 'react-aria-components'
import { useLocation, useNavigation, useSearchParams } from 'react-router'
import { cn } from '#app/utils/misc.tsx'
import { usePreserveNuqsParams } from '#app/utils/navigation.ts'
import { EditIconSetModal } from './application/modals/edit-icon-set-modal'
import { Badge } from './base/badges/badges'
import { Dropdown } from './base/dropdown/dropdown'

function TreeItemActionMenu({
	uploadedId,
	name,
	isVisible,
	isMenuOpen,
	onMenuOpenChange,
}: {
	uploadedId: string
	name: string
	isVisible: boolean
	isMenuOpen: boolean
	onMenuOpenChange: (isOpen: boolean) => void
}) {
	const [isEditModalOpen, setIsEditModalOpen] = useState(false)

	return (
		<>
			<Dropdown.Root isOpen={isMenuOpen} onOpenChange={onMenuOpenChange}>
				<Dropdown.DotsHorizontalButton
					className={cn(
						'hover:bg-quaternary focus-visible:opacity-100 absolute right-2 -m-1 rounded-md p-1',
						isVisible || isMenuOpen ? 'opacity-100' : 'opacity-0',
						isMenuOpen && 'bg-quaternary',
					)}
				/>
				<Dropdown.Popover placement="right" className="w-auto">
					<Dropdown.Menu>
						<Dropdown.Item
							icon={Edit05}
							label="Edit"
							onAction={() => setIsEditModalOpen(true)}
						/>
					</Dropdown.Menu>
				</Dropdown.Popover>
			</Dropdown.Root>
			<EditIconSetModal
				uploadedId={uploadedId}
				initialName={name}
				isOpen={isEditModalOpen}
				onOpenChange={setIsEditModalOpen}
			/>
		</>
	)
}

type MenuTreeNode = {
	svgLogo?: string
	name: string
	link: string
	uploadedSetId?: string
	childItems: MenuTreeNode[]
}

interface TreeMenuProps extends TreeProps<MenuTreeNode> {
	isEditable?: boolean
}

export function TreeMenu({ isEditable, ...props }: TreeMenuProps) {
	const buildUrl = usePreserveNuqsParams(['redirectTo'])

	const location = useLocation()
	const currentSlug = location.pathname

	const navigation = useNavigation()
	const isNavigating = Boolean(navigation.location)
	const nextSlug = navigation.location?.pathname

	const [searchParams] = useSearchParams()
	const searchParamsString = searchParams.toString() // to rerener only after url itself changes after debounce

	// Track which item's menu is currently open
	const [openMenuId, setOpenMenuId] = useState<string | null>(null)

	return (
		<Tree
			aria-label="Icon Sets"
			className="flex flex-col gap-1"
			selectionMode="none"
			{...props}
			dependencies={[currentSlug, nextSlug, searchParamsString, openMenuId]}
		>
			{function renderItem(item) {
				const href = buildUrl(`/${item.link}`)

				const allPossibleActivePaths = currentSlug.split('/').map((_, i) =>
					currentSlug
						.split('/')
						.slice(0, i + 1)
						.join('/'),
				)

				const isActive =
					!isNavigating && allPossibleActivePaths.includes(`/${item.link}`)

				const allPossibleNextPaths = nextSlug?.split('/').map((_, i) =>
					nextSlug
						.split('/')
						.slice(0, i + 1)
						.join('/'),
				)

				const isPending = Boolean(
					isNavigating && allPossibleNextPaths?.includes(`/${item.link}`),
				)

				const isMenuOpen = openMenuId === item.link

				return (
					<TreeItem
						textValue={item.name}
						id={item.link}
						href={href}
						className={({ level }) =>
							cn(
								// Base
								'outline-focus-ring hover:bg-primary_hover relative flex w-full cursor-pointer items-center gap-3 rounded-md py-2 pr-3 transition duration-100 ease-linear select-none focus-visible:z-10 focus-visible:outline-2 focus-visible:outline-offset-2',
								isActive || isPending || isMenuOpen
									? 'bg-primary_hover'
									: 'bg-primary',
								level === 1
									? 'pl-3'
									: `pl-[calc(--spacing(2)+(var(--tree-item-level)_-_1)_*_--spacing(4))]`,
							)
						}
					>
						<TreeItemContent>
							{({
								isExpanded,
								level,
								hasChildItems,
								isFocusVisible,
								isHovered,
							}) => (
								<>
									{/* support right clicking with mouse to open in new tab */}
									{/* only show for non keyboard navigation to not mess with keyboard navigation */}
									{!isFocusVisible ? (
										<Link
											href={href}
											className="absolute inset-0"
											aria-label={item.name}
										></Link>
									) : null}
									<div className="relative size-5 shrink-0">
										{level === 1 ? (
											item.svgLogo ? (
												<span
													className="text-fg-quaternary transition-inherit-all absolute -inset-[1px] size-6 shrink-0 *:size-full"
													dangerouslySetInnerHTML={{
														__html: item.svgLogo,
													}}
												></span>
											) : (
												<Folder className="text-fg-quaternary transition-inherit-all absolute inset-0 size-5 shrink-0" />
											)
										) : null}
										{hasChildItems ? (
											<Button
												slot="chevron"
												className={cn(
													'hover:bg-quaternary absolute inset-0 -m-1 rounded-md p-1 transition duration-100 ease-linear',
													level === 1 ? 'bg-primary_hover' : '',
													isFocusVisible || isHovered || level !== 1
														? 'opacity-100'
														: 'opacity-0',
												)}
											>
												<ChevronRight
													className={cn(
														'text-fg-quaternary transition-inherit-all size-5',
														isExpanded ? 'rotate-90' : '',
													)}
												/>
											</Button>
										) : null}
									</div>
									<span className="flex-1 truncate">{item.name}</span>
									{level === 1 && isEditable && item.uploadedSetId && (
										<TreeItemActionMenu
											uploadedId={item.uploadedSetId}
											name={item.name}
											isVisible={isFocusVisible || isHovered}
											isMenuOpen={isMenuOpen}
											onMenuOpenChange={(open) =>
												setOpenMenuId(open ? item.link : null)
											}
										/>
									)}
									{level === 1 && item.link === 'untitled-ui-icons' && (
										<Badge
											className="absolute right-2"
											type="color"
											color="brand"
											size="sm"
										>
											New
										</Badge>
									)}
								</>
							)}
						</TreeItemContent>
						<Collection items={item.childItems}>{renderItem}</Collection>
					</TreeItem>
				)
			}}
		</Tree>
	)
}
